// Roboflow overlay logic for JamCam (browser version)
// Migrated and adapted from guitar-normal-guy/compositeImage.js and helpers.js

import { Fretboard, GuitarString, Segmentation } from './fretboardClasses';
import { JamCamConfig, defaultJamCamConfig } from './jamcamConfig';
import {
  Point,
  RoboflowPrediction,
  OverlayData,
  DrawSettings,
} from './core/types';
import {
  computeCentroid,
  computeLinearRegression,
  perpendicularDistance,
} from './utils/math';

// Filter predictions for frets and board
export function filterPredictions(
  predictions: RoboflowPrediction[],
  confidenceThreshold = 0.3
) {
  const fretPredictions = predictions
    .filter((pred) => {
      if (!pred.class) return false;
      if (!isNaN(parseInt(pred.class))) {
        const num = parseInt(pred.class);
        return num >= 1 && num <= 15;
      }
      if (pred.class.startsWith('f')) return true;
      return false;
    })
    .filter((pred) => pred.confidence >= confidenceThreshold);

  const boardPredictions = predictions.filter(
    (pred) => pred.class === 'board' && pred.confidence >= confidenceThreshold
  );
  return { fretPredictions, boardPredictions };
}

// Douglas-Peucker simplification (for browser)
export function douglasPeucker(points: Point[], epsilon: number): Point[] {
  if (points.length < 3) return points;
  let dmax = 0,
    index = 0;
  const end = points.length - 1;
  for (let i = 1; i < end; i++) {
    const d = perpendicularDistance(points[i], points[0], points[end]);
    if (d > dmax) {
      index = i;
      dmax = d;
    }
  }
  if (dmax > epsilon) {
    const recResults1: Point[] = douglasPeucker(
      points.slice(0, index + 1),
      epsilon
    );
    const recResults2: Point[] = douglasPeucker(
      points.slice(index, points.length),
      epsilon
    );
    return recResults1.slice(0, recResults1.length - 1).concat(recResults2);
  } else {
    return [points[0], points[end]];
  }
}

// Line segment intersection helper (from guitar-normal-guy)
export function lineSegmentIntersection(
  p: Point,
  d: Point,
  a: Point,
  b: Point
): Point | null {
  const A = d.x;
  const B = -(b.x - a.x);
  const C = d.y;
  const D = -(b.y - a.y);
  const E = a.x - p.x;
  const F = a.y - p.y;
  const denom = A * D - B * C;
  if (Math.abs(denom) < 1e-6) return null;
  const t = (E * D - B * F) / denom;
  const u = (A * F - E * C) / denom;
  if (u < 0 || u > 1) return null;
  return { x: p.x + t * d.x, y: p.y + t * d.y };
}

// Call Roboflow API with a base64 image string
export async function callRoboflow(
  base64Image: string,
  modelUrl: string,
  apiKey: string
): Promise<RoboflowPrediction[]> {
  const url = `${modelUrl}?format=json${apiKey ? `&api_key=${apiKey}` : ''}`;
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: base64Image,
  });
  const data = await response.json();
  return data.predictions || [];
}

// Calculate overlay data (ghost notes, etc.)
export async function calculateOverlayData(
  base64Image: string,
  modelUrl: string,
  apiKey: string
): Promise<OverlayData | {}> {
  const predictions = await callRoboflow(base64Image, modelUrl, apiKey);
  const { fretPredictions, boardPredictions } = filterPredictions(
    predictions,
    0.3
  );
  if (fretPredictions.length > 0) {
    const fretboard = new Fretboard(
      fretPredictions,
      boardPredictions.length > 0 ? boardPredictions[0] : null
    );
    fretboard.calculateTopLineSlope();
    fretboard.calculateBottomLineSlope();
    const simpleStringLines = fretboard.calculateSimpleStringLines(6);
    let ghostData = [];
    for (let i = 0; i < simpleStringLines.length; i++) {
      const line = simpleStringLines[i];
      const gs = new GuitarString(line.slope, line.intercept, i);
      gs.calculateGhostNoteLocations(fretboard.frets);
      ghostData.push({ string: i, ghostNotes: gs.ghostNotes });
    }
    const fretBoardLine = [
      { x: 0, y: 0 },
      { x: 0, y: 0 },
    ];
    const fretBoardLength = fretboard.fretBoardLength;
    return {
      ghostData,
      fretBoardLine,
      fretBoardLength,
      fretboard, // Include the fretboard for drawing
      boardPredictions, // Include board predictions for drawing
    };
  }
  return {};
}

// Draw overlays on a canvas
export function drawOverlay(
  ctx: CanvasRenderingContext2D,
  overlayData: any,
  settings: {
    color?: string;
    scaleX?: number;
    scaleY?: number;
    config?: JamCamConfig;
  } = {}
) {
  if (!overlayData.fretboard) return;

  const scaleX = settings.scaleX || 1;
  const scaleY = settings.scaleY || 1;
  const config = settings.config || defaultJamCamConfig;
  const fretboard = overlayData.fretboard as Fretboard;

  // Apply scaling to the canvas context
  ctx.save();
  ctx.scale(scaleX, scaleY);

  // Draw fret segmentation overlays (exactly like guitar-normal-guy)
  if (config.display.segmentations.fret.show) {
    fretboard.frets
      .filter(
        (fret) => fret.confidence >= config.settings.frets.confidenceThreshold
      )
      .forEach((fret) => {
        const seg = new Segmentation(fret.points);

        // Draw fill first (behind border)
        if (config.display.segmentations.fret.fill.show) {
          seg.drawFill(ctx, config.display.segmentations.fret.fill);
        }

        // Draw border
        if (config.display.segmentations.fret.border.show) {
          seg.drawBorder(ctx, config.display.segmentations.fret.border);
        }

        // Draw centroid
        if (config.display.segmentations.fret.centroid.show) {
          seg.drawCentroid(ctx, {
            color: config.display.segmentations.fret.centroid.color,
            radius: config.display.segmentations.fret.centroid.diameter / 2,
            opacity: config.display.segmentations.fret.centroid.opacity,
          });
        }

        // Draw Douglas-Peucker outline
        if (config.display.segmentations.fret.douglasPeucker.show) {
          seg.drawDouglasPeucker(
            ctx,
            config.settings.frets.douglasPeuckerEpsilon,
            config.display.segmentations.fret.douglasPeucker
          );
        }
      });
  }

  // Draw board overlay
  if (config.display.segmentations.board.show && overlayData.boardPredictions) {
    overlayData.boardPredictions
      .filter(
        (pred: any) =>
          pred.confidence >= config.settings.board.confidenceThreshold
      )
      .forEach((pred: any) => {
        const seg = new Segmentation(pred.points);

        // Draw fill first (behind border)
        if (config.display.segmentations.board.fill.show) {
          seg.drawFill(ctx, config.display.segmentations.board.fill);
        }

        // Draw border
        if (config.display.segmentations.board.border.show) {
          seg.drawBorder(ctx, config.display.segmentations.board.border);
        }
      });
  }

  // Draw fretboard lines
  if (config.display.fretboard.topLine.show) {
    fretboard.drawTopLine(ctx, config.display.fretboard.topLine);
  }
  if (config.display.fretboard.bottomLine.show) {
    fretboard.drawBottomLine(ctx, config.display.fretboard.bottomLine);
  }
  if (config.display.fretboard.strings.show) {
    fretboard.drawSimpleStringLines(ctx, {
      ...config.display.fretboard.strings,
      numStrings: config.display.fretboard.strings.count,
    });
  }
  if (config.display.fretboard.centroidsLinearRegression.show) {
    fretboard.drawFretCentroidsLinearRegression(ctx, {
      ...config.display.fretboard.centroidsLinearRegression,
      confidenceThreshold: config.settings.frets.confidenceThreshold,
    });
  }
  if (config.display.fretboard.perpendicularLines.show) {
    fretboard.drawPerpendicularFretLines(ctx, {
      ...config.display.fretboard.perpendicularLines,
      confidenceThreshold: config.settings.frets.confidenceThreshold,
    });
  }

  // Draw ghost notes
  const simpleStringLines = fretboard.calculateSimpleStringLines(
    config.settings.strings.count
  );

  // Clear existing fret notes to avoid accumulation
  fretboard.frets.forEach((fret) => {
    fret.fretNotes = [];
  });

  let guitarStrings = [];
  for (let i = 0; i < simpleStringLines.length; i++) {
    const line = simpleStringLines[i];
    const gs = new GuitarString(line.slope, line.intercept, i);
    guitarStrings.push(gs);
  }

  // Handle gap filling if enabled
  let estimatedFrets: any[] = [];
  if (config.display.notes.ghost.gapFilling.enabled) {
    estimatedFrets = fretboard.estimateMissingFrets(
      config.display.notes.ghost.gapFilling.maxFretsToEstimate
    );

    // Calculate ghost notes for both detected and estimated frets
    guitarStrings.forEach((gs) => {
      gs.calculateGhostNoteLocationsWithEstimated(
        fretboard.frets,
        estimatedFrets
      );
    });
  } else {
    // Use aligned method or original method based on config (existing behavior)
    if (config.display.notes.ghost.useAlignedMethod) {
      GuitarString.calculateAlignedGhostNoteLocations(
        fretboard.frets,
        guitarStrings
      );
    } else {
      guitarStrings.forEach((gs) => {
        gs.calculateGhostNoteLocations(fretboard.frets);
      });
    }
  }

  // Draw guitar strings (optional)
  if (config.display.fretboard.strings.show) {
    guitarStrings.forEach((gs) => {
      gs.draw(ctx, config.display.fretboard.strings);
    });
  }

  // Draw estimated fret segmentations if gap filling is enabled
  if (
    config.display.notes.ghost.gapFilling.enabled &&
    config.display.notes.ghost.gapFilling.estimatedFrets.show
  ) {
    estimatedFrets.forEach((fret) => {
      const seg = new Segmentation(fret.points);

      // Draw estimated fret with different styling
      seg.drawBorder(ctx, {
        ...config.display.notes.ghost.gapFilling.estimatedFrets,
        color: config.display.notes.ghost.gapFilling.estimatedFrets.color,
        opacity: config.display.notes.ghost.gapFilling.estimatedFrets.opacity,
        style:
          config.display.notes.ghost.gapFilling.estimatedFrets.border.style,
      });
    });
  }

  // Draw ghost notes (avoid duplicates - only show one type)
  if (config.display.notes.ghost.show) {
    guitarStrings.forEach((gs, stringIdx) => {
      // Draw detected ghost notes
      gs.drawGhostNotes(ctx, config.display.notes.ghost);

      // Draw estimated ghost notes if gap filling is enabled
      if (
        config.display.notes.ghost.gapFilling.enabled &&
        config.display.notes.ghost.gapFilling.estimatedNotes.show
      ) {
        gs.drawEstimatedGhostNotes(
          ctx,
          config.display.notes.ghost.gapFilling.estimatedNotes
        );
      }

      // Draw note names if enabled (for both detected and estimated)
      if (
        config.display.notes.showNames ||
        config.display.notes.ghost.names.show
      ) {
        const font = config.display.notes.ghost.names.font;
        ctx.save();
        ctx.font = `${font.size || 10}px ${font.style || 'Arial'}`;
        ctx.fillStyle = font.color || 'white';
        ctx.globalAlpha = font.opacity !== undefined ? font.opacity : 0.8;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';

        // Draw names for detected notes
        gs.ghostNotes.forEach((pt, fretIdx) => {
          if (
            pt &&
            typeof pt.x === 'number' &&
            typeof pt.y === 'number' &&
            !isNaN(pt.x) &&
            !isNaN(pt.y)
          ) {
            const label = `${fretIdx + 1}`;
            const offsetY = (config.display.notes.ghost.diameter || 8) * 0.7;
            ctx.fillText(label, pt.x, pt.y - offsetY);
          }
        });

        // Draw names for estimated notes (with different styling)
        if (config.display.notes.ghost.gapFilling.enabled) {
          ctx.fillStyle =
            config.display.notes.ghost.gapFilling.estimatedNotes.color ||
            'rgba(255,255,0,0.8)';
          gs.estimatedGhostNotes.forEach((pt, fretIdx) => {
            if (
              pt &&
              typeof pt.x === 'number' &&
              typeof pt.y === 'number' &&
              !isNaN(pt.x) &&
              !isNaN(pt.y)
            ) {
              const label = `E${fretIdx + 1}`; // "E" for estimated
              const offsetY =
                (config.display.notes.ghost.gapFilling.estimatedNotes
                  .diameter || 6) * 0.7;
              ctx.fillText(label, pt.x, pt.y - offsetY);
            }
          });
        }

        ctx.restore();
      }
    });
  } else if (config.display.notes.fret.show) {
    fretboard.frets.forEach((fret) => {
      fret.drawFretNotes(ctx, config.display.notes.fret);
    });
  }

  ctx.restore();
}
